import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  List,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Paper,
  Tooltip,
  CardHeader,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Help as HelpIcon,
} from "@mui/icons-material";
import {
  successAlert,
  errorAlert,
  confirmAlert,
  loadingAlert,
  closeAlert,
} from "../../utils/swalConfig";
import { styled } from "@mui/material/styles";

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const HeaderIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 40,
  height: 40,
}));

const FaqItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: "transform 0.2s",
  cursor: "grab",
  "&:hover": {
    transform: "translateX(4px)",
  },
}));

const SupportManagement = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    whatsappNumber: "",
    supportEmail: "",
    faqs: [],
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({ question: "", answer: "" });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await fetchSupportSettings();
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem("jwt");
          localStorage.removeItem("userRole");
          navigate("/login");
        }
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchSupportSettings = async () => {
    try {
      loadingAlert("Loading support settings...");
      const response = await api.get("/admin/support");
      if (response.data.status === "success") {
        setSettings(response.data.data);
        closeAlert();
      }
    } catch (err) {
      closeAlert();
      console.error("Error fetching support settings:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem("jwt");
        localStorage.removeItem("userRole");
        navigate("/login");
      }
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to fetch support settings"
      );
    }
  };

  const handleUpdateSettings = async () => {
    try {
      loadingAlert("Updating support settings...");
      await api.patch("/admin/support", {
        whatsappNumber: settings.whatsappNumber,
        supportEmail: settings.supportEmail,
      });
      closeAlert();
      successAlert("Success", "Support settings updated successfully");
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to update settings"
      );
    }
  };

  const handleAddFaq = () => {
    setEditingFaq(null);
    setFaqForm({ question: "", answer: "" });
    setOpenDialog(true);
  };

  const handleEditFaq = (faq) => {
    setEditingFaq(faq);
    setFaqForm({ question: faq.question, answer: faq.answer });
    setOpenDialog(true);
  };

  const handleSaveFaq = async () => {
    try {
      loadingAlert(editingFaq ? "Updating FAQ..." : "Adding new FAQ...");
      if (editingFaq) {
        await api.patch(`/admin/support/faqs/${editingFaq._id}`, faqForm);
      } else {
        await api.post("/admin/support/faqs", faqForm);
      }
      closeAlert();
      successAlert(
        "Success",
        `FAQ ${editingFaq ? "updated" : "added"} successfully`
      );
      setOpenDialog(false);
      fetchSupportSettings();
    } catch (err) {
      closeAlert();
      errorAlert("Error", err.response?.data?.message || "Failed to save FAQ");
    }
  };

  const handleDeleteFaq = async (faqId) => {
    const result = await confirmAlert(
      "Delete FAQ",
      "Are you sure you want to delete this FAQ?",
      "warning"
    );

    if (result.isConfirmed) {
      try {
        loadingAlert("Deleting FAQ...");
        await api.delete(`/admin/support/faqs/${faqId}`);
        closeAlert();
        successAlert("Success", "FAQ deleted successfully");
        fetchSupportSettings();
      } catch (err) {
        closeAlert();
        errorAlert(
          "Error",
          err.response?.data?.message || "Failed to delete FAQ"
        );
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mb: 4,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <HelpIcon sx={{ fontSize: 32, color: "primary.main" }} />
        Support Management
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader
              avatar={
                <HeaderIcon>
                  <WhatsAppIcon />
                </HeaderIcon>
              }
              title="Contact Settings"
              subheader="Manage support contact information"
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                label="WhatsApp Number"
                value={settings.whatsappNumber}
                onChange={(e) =>
                  setSettings({ ...settings, whatsappNumber: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <WhatsAppIcon sx={{ color: "#25D366", mr: 1 }} />
                  ),
                }}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Support Email"
                value={settings.supportEmail}
                onChange={(e) =>
                  setSettings({ ...settings, supportEmail: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <EmailIcon sx={{ color: "primary.main", mr: 1 }} />
                  ),
                }}
                sx={{ mb: 3 }}
              />
              <Button
                variant="contained"
                onClick={handleUpdateSettings}
                fullWidth
                size="large"
                sx={{
                  mt: 2,
                  background:
                    "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                  boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                }}
              >
                Update Settings
              </Button>
            </CardContent>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader
              avatar={
                <HeaderIcon>
                  <HelpIcon />
                </HeaderIcon>
              }
              title="FAQs Management"
              subheader="Manage frequently asked questions"
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddFaq}
                  sx={{
                    background:
                      "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                  }}
                >
                  Add FAQ
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <List sx={{ minHeight: 100 }}>
                {settings.faqs.map((faq) => (
                  <FaqItem key={faq._id}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 500 }}
                        >
                          {faq.question}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {faq.answer.substring(0, 100)}...
                        </Typography>
                      </Box>
                      <Box>
                        <Tooltip title="Edit FAQ">
                          <IconButton onClick={() => handleEditFaq(faq)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete FAQ">
                          <IconButton
                            onClick={() => handleDeleteFaq(faq._id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </FaqItem>
                ))}
              </List>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* FAQ Dialog with enhanced styling */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          {editingFaq ? "Edit FAQ" : "Add New FAQ"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Question"
            value={faqForm.question}
            onChange={(e) =>
              setFaqForm({ ...faqForm, question: e.target.value })
            }
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Answer"
            value={faqForm.answer}
            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
            multiline
            rows={6}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveFaq}
            sx={{
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
            }}
          >
            {editingFaq ? "Update" : "Add"} FAQ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportManagement;
